import React, { useEffect, useCallback, lazy, Suspense } from "react";
import "./App.css";
import "react-datepicker/dist/react-datepicker.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { setAuth } from "./store/authSlice";
import { useDispatch } from "react-redux";
// utils
import AdminRoute from "./utils/AdminRoute";
import GuestRoute from "./utils/GuestRoute";
import SemiProtected from "./utils/SemiProtected";
import ScrollToTop from "./utils/ScrollToTop";
import PartialRenderRoute from "./utils/PartialRenderRoute";
import FavoriteRoute from "./utils/FavoriteRoute";
import { AppClientGoogleLogin } from "./http/index";
import DownloadAppController from "./components/DownloadAppPopup/DownloadAppController";
import Loader from "./components/shared/Loader";
import ThemeHandler from "./components/shared/ThemeHandler";

// Lazy-loaded pages (improves bundle size, loads on demand)
const Home = lazy(() => import("./pages/User/Home/Home"));
const Login = lazy(() => import("./pages/User/Login/Login"));
const Signup = lazy(() => import("./pages/User/Signup/Signup"));
const AboutUs = lazy(() => import("./pages/User/AboutUs/AboutUs"));
const Contactus = lazy(() => import("./pages/User/Contactus/Contactus"));
const Profile = lazy(() => import("./pages/User/Profile/Profile"));
const PwaProfile = lazy(() => import("./pages/User/Profile/PwaProfile"));
const WhereToMap = lazy(() => import("./pages/User/WhereToMap/WhereToMap"));
const Events = lazy(() => import("./pages/User/Events/Events"));
const EventDescription = lazy(
  () => import("./pages/User/EventDescription/EventDescription"),
);
const VenueDescription = lazy(
  () => import("./pages/User/VenueDescription/VenueDescription"),
);
const Favorites = lazy(() => import("./pages/User/Favorites/Favorites"));
const PastPurchase = lazy(
  () => import("./pages/User/PastPurchase/PastPurchase"),
);
const FAQ = lazy(() => import("./pages/User/FAQ/FAQ"));
const BookTicket = lazy(() => import("./pages/User/BookTicket/BookTicket"));
const TicketStatus = lazy(
  () => import("./pages/User/TicketStatus/TicketStatus"),
);
const VerifyUserAccount = lazy(
  () => import("./pages/User/VerifyUserAccount/VerifyUserAccount"),
);
const VendorLogin = lazy(() => import("./pages/User/Login/VendorLogin"));
const VendorSignup = lazy(() => import("./pages/User/Signup/VendorSignup"));
const VendorNotification = lazy(
  () => import("./pages/Vendor/VendorNotification/VendorNotification"),
);
const VendorActivation = lazy(
  () => import("./pages/Vendor/VendorActivation/VendorActivation"),
);
const VendorHostedEvents = lazy(
  () => import("./pages/Vendor/VendorHostedEvents/VendorHostedEvents"),
);
const VendorHome = lazy(() => import("./pages/Vendor/VendorHome/VendorHome"));
const VendorEventDescripton = lazy(
  () => import("./pages/Vendor/VendorEventDescription/VendorEventDescripton"),
);
const VendorBookedTickets = lazy(
  () => import("./pages/Vendor/VendorBookedTickets/VendorBookedTickets"),
);
const VendorProfile = lazy(() => import("./pages/User/Profile/VendorProfile"));
const AdminHome = lazy(() => import("./pages/AdminDashboard/AdminHome"));
const EventsPWA = lazy(() => import("./pages/User/PWA-Events/EventsPWA"));
const AdminCategory = lazy(
  () => import("./pages/AdminDashboard/AdminCategory"),
);
const AdminUsers = lazy(() => import("./pages/AdminDashboard/AdminUsers"));
const AdminVendors = lazy(() => import("./pages/AdminDashboard/AdminVendors"));
const AdminEvents = lazy(() => import("./pages/AdminDashboard/AdminEvents"));
const AdminOffers = lazy(() => import("./pages/AdminDashboard/AdminOffers"));
const AdminEventDescription = lazy(
  () => import("./pages/AdminDashboard/AdminEventDescription"),
);
const AdminBookedTickets = lazy(
  () => import("./pages/AdminDashboard/AdminBookedTickets"),
);
const SearchPWA = lazy(() => import("./pages/User/SearchPWA/SearchPWA"));
const AdminCheckProfile = lazy(
  () => import("./pages/AdminDashboard/AdminCheckProfile"),
);
const UserNotification = lazy(
  () => import("./pages/User/UserNotification/UserNotification"),
);
const VerifyVendorAccount = lazy(
  () => import("./pages/User/VerifyUserAccount/VerifyVendorAccount"),
);
const Notifications = lazy(
  () => import("./pages/AdminDashboard/Notifications"),
);
const NotFound = lazy(() => import("./pages/NotFound"));
const InputEmail = lazy(() => import("./pages/User/ForgotPassword/InputEmail"));
const ResetPassword = lazy(
  () => import("./pages/User/ForgotPassword/ResetPassword"),
);
const AdminVenue = lazy(() => import("./pages/AdminDashboard/AdminVenue"));
const Cookies = lazy(() => import("./pages/User/LegalCorner/Cookies"));
const PrivacyPolicy = lazy(
  () => import("./pages/User/LegalCorner/PrivacyPolicy"),
);
const TermsAndCondition = lazy(
  () => import("./pages/User/LegalCorner/TermsAndCondition"),
);
const InputEmailVendor = lazy(
  () => import("./pages/Vendor/ForgotPasswordVendor/InputEmailVendor"),
);
const ResetPasswordVendor = lazy(
  () => import("./pages/Vendor/ForgotPasswordVendor/ResetPasswordVendor"),
);
const Categories = lazy(() => import("./pages/User/Categories/Categories"));
const WidgetBooking = lazy(
  () => import("./pages/User/WidgetBooking/WidgetBooking"),
);
const Testing = lazy(() => import("./pages/User/Testing/Testing"));
const CoffeePartyDemo = lazy(() => import("./pages/User/Testing/CoffeePartyDemo"));
const AdminDining = lazy(() => import("./pages/AdminDashboard/AdminDining"));
const AdminHomepageSections = lazy(() => import("./pages/AdminDashboard/AdminHomepageSections"));
const Dining = lazy(() => import("./pages/User/Dining/Dining"));
const DiningDetails = lazy(() => import("./pages/User/Dining/DiningDetails"));
// It's better to define event handler callbacks once and clean them up properly.
function App() {
  const dispatch = useDispatch();
  // Handler for storing expo token
  const handleSetToken = useCallback(() => {
    try {
      if (window && window.expoToken) {
        window.localStorage.setItem("expoToken", window.expoToken);
      }
    } catch (err) {
      // swallow silently in production; consider logging to an error service
      // console.error('Failed to set expo token', err);
    }
  }, []);
  // Handler for tokens ready (login flow)
  const handleTokensReady = useCallback(async () => {
    const accessToken = window?.accessToken;
    const refreshToken = window?.refreshToken;
    if (accessToken && refreshToken) {
      await loginToWebsite(accessToken, refreshToken);
    }
  }, [dispatch]);
  useEffect(() => {
    window.addEventListener("setToken", handleSetToken);
    window.addEventListener("tokensReady", handleTokensReady);
    return () => {
      window.removeEventListener("setToken", handleSetToken);
      window.removeEventListener("tokensReady", handleTokensReady);
    };
  }, [handleSetToken, handleTokensReady]);
  const loginToWebsite = useCallback(
    async (accessToken, refreshToken) => {
      const data = { accessToken, refreshToken };
      try {
        const res = await toast.promise(AppClientGoogleLogin(data), {
          loading: "Logging in...",
          success: "Login successful!",
        });
        // If running inside a native container, notify it
        if (window?.isNative && window?.ReactNativeWebView) {
          const message = JSON.stringify({ action: "refresh" });
          window.ReactNativeWebView.postMessage(message);
        }
        if (res?.data) {
          dispatch(setAuth(res.data));
        }
      } catch (err) {
        // Consider sending to a monitoring/tracking service
        // console.error('Login via AppClientGoogleLogin failed', err);
      }
    },
    [dispatch],
  );
  // Route configuration — easier to read and maintain
  const routes = [
    { path: "/", element: <Home /> },
    {
      path: "/user/my-account/",
      element: <FavoriteRoute Component={PwaProfile} />,
    },
    { path: "/my-account/", element: <SemiProtected Component={Profile} /> },
    { path: "/favorites", element: <Favorites /> },
    { path: "/category/:category", element: <Events /> },
    { path: "/sign-up", element: <Signup /> },
    { path: "/login", element: <Login /> },
    { path: "/user/verify-account/:token", element: <VerifyUserAccount /> },
    { path: "/about", element: <AboutUs /> },
    { path: "/whereto", element: <WhereToMap /> },
    { path: "/contact", element: <Contactus /> },
    { path: "/events/:eventid", element: <EventDescription /> },
    {
      path: "/bookticket/:eventid",
      element: <SemiProtected Component={BookTicket} />,
    },
    { path: "/verifyaccount/:token", element: <VerifyUserAccount /> },
    { path: "/reset", element: <InputEmail /> },
    { path: "/user/reset-password/:token", element: <ResetPassword /> },
    { path: "/venue/:venueid", element: <VenueDescription /> },
    {
      path: "/pastpurchase",
      element: <SemiProtected Component={PastPurchase} />,
    },
    { path: "/faq", element: <FAQ /> },
    { path: "/ticketstatus/:ticketid", element: <TicketStatus /> },
    { path: "/event", element: <EventsPWA /> },
    { path: "/searchpage", element: <SearchPWA /> },
    {
      path: "/user/notification",
      element: <PartialRenderRoute Component={UserNotification} />,
    },
    { path: "/user/cookies", element: <Cookies /> },
    { path: "/user/privacypolicy", element: <PrivacyPolicy /> },
    { path: "/user/termsandconditions", element: <TermsAndCondition /> },
    { path: "/category", element: <Categories /> },
    { path: "/widget", element: <WidgetBooking /> },
    { path: "/testing", element: <Testing /> },
    { path: "/testing/coffee-party-demo", element: <CoffeePartyDemo /> },
    // vendor
    { path: "/vendor/login", element: <Login /> },
    { path: "/vendor/sign-up", element: <VendorSignup /> },
    { path: "/vendor/home", element: <GuestRoute Component={VendorHome} /> },
    { path: "/vendor/activation", element: <VendorActivation /> },
    {
      path: "/vendor/hostedevents",
      element: <GuestRoute Component={VendorHostedEvents} />,
    },
    {
      path: "/vendor/event/:eventid",
      element: <GuestRoute Component={VendorEventDescripton} />,
    },
    {
      path: "/vendor/my-account",
      element: <GuestRoute Component={VendorProfile} />,
    },
    {
      path: "/vendor/:eventid/bookedtickets",
      element: <GuestRoute Component={VendorBookedTickets} />,
    },
    { path: "/vendor/notification", element: <VendorNotification /> },
    { path: "/vendor/verify-account/:token", element: <VerifyVendorAccount /> },
    { path: "/vendor/reset", element: <InputEmailVendor /> },
    { path: "/vendor/reset-password/:token", element: <ResetPasswordVendor /> },
    // admin
    { path: "/admin/profile/:userid", element: <AdminCheckProfile /> },
    { path: "/admin/home", element: <AdminRoute Component={AdminHome} /> },
    { path: "/admin/users", element: <AdminRoute Component={AdminUsers} /> },
    {
      path: "/admin/vendors",
      element: <AdminRoute Component={AdminVendors} />,
    },
    { path: "/admin/events", element: <AdminRoute Component={AdminEvents} /> },
    { path: "/admin/offers", element: <AdminRoute Component={AdminOffers} /> },
    {
      path: "/admin/event/:eventid",
      element: <AdminRoute Component={AdminEventDescription} />,
    },
    {
      path: "/admin/:eventid/bookedtickets",
      element: <AdminRoute Component={AdminBookedTickets} />,
    },
    {
      path: "/admin/categories",
      element: <AdminRoute Component={AdminCategory} />,
    },
    { path: "/admin/venue", element: <AdminRoute Component={AdminVenue} /> },
    {
      path: "/admin/notifications",
      element: <AdminRoute Component={Notifications} />,
    },
    { path: "/admin/notifications", element: <AdminRoute Component={Notifications} /> },

    // Dining Module
    { path: "/admin/dining", element: <AdminRoute Component={AdminDining} /> },
    { path: "/admin/homepage", element: <AdminRoute Component={AdminHomepageSections} /> },
    { path: "/dining", element: <Dining /> },
    { path: "/dining/:slug", element: <DiningDetails /> },

    { path: "*", element: <NotFound /> },
  ];
  return (
    <div className="App !dark:bg-[#2c2c2c] dark:text-white">
      <Toaster />
      <Router>
        <ThemeHandler />
        <DownloadAppController />
        <ScrollToTop />
        <Suspense fallback={<Loader />}>
          <Routes>
            {routes.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
          </Routes>
        </Suspense>
      </Router>
    </div>
  );
}
export default App;
